import { useCallback, useEffect, useMemo } from 'react'

import { Box, Button, Card, Center, Flex, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconAlertOctagonFilled,
  IconArrowNarrowLeft,
  IconCheck,
  IconEye,
  IconPencil,
  IconUserCancel,
} from '@tabler/icons-react'
import { Title as RATitle, useTranslate } from 'react-admin'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetApiUsersId, usePatchApiUsersIdRoles } from '../../codegen/generated/users'
import { LXLoader } from '../../components/LXLoader'
import { useUserStore } from '../../stores/useUserStore'
import { InformationsUser } from './InformationsUser'
import { UpdateUser } from './UpdateUser'

type UserProps = {
  isEdit?: boolean
}

export const User = ({ isEdit = false }: UserProps) => {
  const { userId } = useParams()
  const t = useTranslate()
  const navigate = useNavigate()
  const userStore = useUserStore()

  const userQuery = useGetApiUsersId(userId ?? '', {
    query: { enabled: !!userId },
  })
  const patchUserRolesMutation = usePatchApiUsersIdRoles()

  const user = useMemo(() => userQuery.data?.body, [userQuery.data?.body])
  const title = useMemo(() => `${t('pages.users._title')} / ${user?.email ?? ''}`, [t, user])

  const onUpdate = useCallback(() => {
    userQuery.refetch()

    showNotification({
      color: 'green',
      icon: <IconCheck />,
      title: t('pages.users.success.userUpdate.title'),
      message: t('pages.users.success.userUpdate.message'),
    })
  }, [userQuery.refetch, t])

  const onDeactivate = useCallback(
    () =>
      modals.openConfirmModal({
        title: (
          <Text fw="bold" size="h3">
            {t('pages.users.disableUser')}
          </Text>
        ),
        children: (
          <Center>
            <Flex align="center" my="md">
              <IconAlertOctagonFilled color="orange" alignmentBaseline="baseline" />
              <Text ml="sm" c="orange" fw="bold">
                {t('pages.users.confirm.wantToDisableUser')}
              </Text>
            </Flex>
          </Center>
        ),
        labels: {
          confirm: t('_generics.disable'),
          cancel: t('_generics.cancel'),
        },
        cancelProps: { variant: 'subtle' },
        confirmProps: {
          variant: 'filled',
          leftSection: <IconAlertOctagonFilled size={16} />,
          color: 'orange',
        },
        onConfirm: async () => {
          try {
            if (user?.id) {
              await patchUserRolesMutation.mutateAsync({
                id: user?.id,
                data: {
                  roleIds: [],
                },
              })

              navigate('/users-roles/users/')

              showNotification({
                color: 'green',
                icon: <IconCheck />,
                title: t('pages.users.success.userDeactivate.title'),
                message: t('pages.users.success.userDeactivate.message'),
              })
            }
          } catch (e) {
            console.error(e)
            // showErrorNotification(e)
          }
        },
      }),
    [patchUserRolesMutation, navigate, user?.id, t],
  )

  if (userQuery.isLoading) {
    return (
      <Box w="100%">
        <LXLoader />
      </Box>
    )
  }

  return (
    <Box w="100%">
      <RATitle title={title} />
      <Flex justify="space-between" align="center">
        <Button
          type="button"
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowNarrowLeft size={16} />}
          onClick={() => navigate('/users-roles/users/')}
        >
          <Text size="sm" tt="uppercase">
            {t('_generics.backToList')}
          </Text>
        </Button>
        <Box>
          {isEdit === true ? (
            <Button
              type="button"
              variant="subtle"
              size="compact-sm"
              leftSection={<IconEye size={16} />}
              onClick={() => navigate(`/users-roles/users/${userId}`)}
            >
              <Text size="sm" tt="uppercase">
                {t('_generics.display')}
              </Text>
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="subtle"
                size="compact-sm"
                leftSection={<IconPencil size={16} />}
                disabled={user?.id === userStore.me?.id}
                onClick={() => {
                  navigate(`/users-roles/users/${userId}/edit`)
                }}
              >
                <Text size="sm" tt="uppercase">
                  {t('_generics.edit')}
                </Text>
              </Button>
              <Button
                type="button"
                variant="subtle"
                size="compact-sm"
                leftSection={<IconUserCancel size={16} />}
                color="orange"
                onClick={onDeactivate}
                disabled={user?.roles?.length === 0 || user?.id === userStore.me?.id}
              >
                <Text size="sm" tt="uppercase">
                  {t('_generics.disable')}
                </Text>
              </Button>
            </>
          )}
        </Box>
      </Flex>
      <Card shadow="xs" p="xl">
        {isEdit ? (
          <UpdateUser user={userQuery.data?.body} onUpdate={onUpdate} />
        ) : (
          <InformationsUser user={user} />
        )}
      </Card>
    </Box>
  )
}
